/**
 * Teslyn Cloud's calls for the pre-flight briefs and safety meetings (R161; decisions 916, 917, 974;
 * docs/specs/safety-brief.md §5, §6; 20261103110000_safety_brief.sql). Briefs are made on Teslyn Mobile; here staff
 * read them (RLS: every staff role of the project), the project's client reads only each brief's PDF through
 * my_safety_briefs while the project's setting is on, and tenant admins, managers and field crew change the
 * project's crew check list and that setting (brief_setting, one row per project, its id the project's).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DEFAULT_BRIEF_SETTING_CHECKS,
  EMPTY_BRIEF_SNAPSHOT,
  type BriefKind,
  type BriefSetting,
  type BriefSettingChecks,
  type SafetyAction,
  type SafetyBrief,
  type SafetyBriefSign,
} from '@teslyn/domain';
import { missingView, plainPortalError, type PortalProject } from '../portal/api.ts';

const fail = (e: { message: string; code?: string } | null, fallback: string): never => {
  throw new Error(e ? plainPortalError(e) : fallback);
};

/** A function Teslyn Cloud does not have yet (the migration not applied). */
const missingFunction = (e: { readonly code?: string; readonly message?: string } | null): boolean =>
  Boolean(
    e &&
    (e.code === 'PGRST202' ||
      e.code === '42883' ||
      /could not find the function|function .* does not exist/i.test(e.message ?? '')),
  );

/** The brief's PDF as Teslyn Cloud holds it (project_file kind brief_pdf). */
export interface BriefPdf {
  readonly object_path: string;
  readonly file_name: string;
  readonly size_bytes: number;
}

export interface ProjectBriefs {
  readonly briefs: readonly SafetyBrief[];
  readonly signs: readonly SafetyBriefSign[];
  readonly actions: readonly SafetyAction[];
  /** By brief id. */
  readonly pdfs: Readonly<Record<string, BriefPdf>>;
  readonly setting: BriefSetting | null;
}

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** Rows as Postgres gives them, with the JSON parts the domain expects never missing. */
function brief(r: Record<string, unknown>): SafetyBrief {
  const b = r as unknown as SafetyBrief;
  return {
    ...b,
    attendees: arr(b.attendees),
    snapshot: { ...EMPTY_BRIEF_SNAPSHOT, ...((b.snapshot as object | null) ?? {}) },
    hazards: arr(b.hazards),
    checks: arr(b.checks),
    conditions: b.conditions ?? { reviewed: false, note: '' },
    items: arr(b.items),
    notes: b.notes ?? '',
    leader_name: b.leader_name ?? '',
    location: b.location ?? '',
  };
}

function setting(r: Record<string, unknown> | null): BriefSetting | null {
  if (!r) return null;
  const s = r as unknown as BriefSetting;
  const c = (s.checks ?? DEFAULT_BRIEF_SETTING_CHECKS) as BriefSettingChecks;
  return {
    ...s,
    checks: { selected: arr<string>(c.selected), custom: arr(c.custom) },
    client_visible: s.client_visible === true || (s.client_visible as unknown) === 1,
  };
}

/**
 * Every brief and meeting of the project with its signs, the project's actions, the PDFs and the setting (staff).
 * Null when Teslyn Cloud does not have the briefs yet (20261103110000 not applied).
 */
export async function loadProjectBriefs(
  sb: SupabaseClient,
  projectId: string,
): Promise<ProjectBriefs | null> {
  const [b, s, a, f, st] = await Promise.all([
    sb.from('safety_brief').select('*').eq('project_id', projectId).is('deleted_at', null),
    sb.from('safety_brief_sign').select('*').eq('project_id', projectId).is('deleted_at', null),
    sb.from('safety_action').select('*').eq('project_id', projectId).is('deleted_at', null),
    sb
      .from('project_file')
      .select('record_id, object_path, file_name, size_bytes')
      .eq('project_id', projectId)
      .eq('kind', 'brief_pdf'),
    sb.from('brief_setting').select('*').eq('project_id', projectId).maybeSingle(),
  ]);
  if (missingView(b.error)) return null;
  if (b.error) fail(b.error, 'The briefs could not be read.');
  if (s.error && !missingView(s.error)) fail(s.error, 'The signs could not be read.');
  if (a.error && !missingView(a.error)) fail(a.error, 'The actions could not be read.');
  const pdfs: Record<string, BriefPdf> = {};
  for (const r of arr<{ record_id: string; object_path: string; file_name: string; size_bytes: number }>(
    f.error ? [] : f.data,
  ))
    pdfs[r.record_id] = { object_path: r.object_path, file_name: r.file_name, size_bytes: r.size_bytes };
  return {
    briefs: arr<Record<string, unknown>>(b.data).map(brief),
    signs: arr<SafetyBriefSign>(s.error ? [] : s.data).map((x) => ({ ...x, checks: arr<string>(x.checks) })),
    actions: arr<SafetyAction>(a.error ? [] : a.data),
    pdfs,
    setting: st.error ? null : setting((st.data as Record<string, unknown> | null) ?? null),
  };
}

/** A brief's PDF as the client may have it (my_safety_briefs). */
export interface ClientBrief {
  readonly id: string;
  readonly kind: BriefKind;
  readonly brief_date: string;
  readonly file_name: string;
  readonly storage_path: string;
  readonly size_bytes: number | null;
  readonly made_at: string | null;
}

/** The briefs' PDFs shown to the project's client: none while the project's setting is off. */
export async function loadClientBriefs(sb: SupabaseClient, projectId: string): Promise<ClientBrief[]> {
  const { data, error } = await sb.rpc('my_safety_briefs', { p_project: projectId });
  if (error) {
    if (missingFunction(error)) return [];
    fail(error, 'The briefs could not be read.');
  }
  return arr<ClientBrief>(data);
}

/** A 5-minute link to a brief's PDF in bucket safety. */
export async function briefPdfUrl(sb: SupabaseClient, path: string, fileName: string): Promise<string> {
  const { data, error } = await sb.storage
    .from('safety')
    .createSignedUrl(path, 300, { download: fileName || true });
  if (error || !data) fail(error, 'The PDF could not be opened.');
  return data!.signedUrl;
}

/** The project's setting, null when there is no row yet (the defaults), 'missing' before the migration. */
export async function loadBriefSetting(
  sb: SupabaseClient,
  projectId: string,
): Promise<BriefSetting | null | 'missing'> {
  const { data, error } = await sb
    .from('brief_setting')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) {
    if (missingView(error)) return 'missing';
    fail(error, 'The pre-flight brief settings could not be read.');
  }
  return setting((data as Record<string, unknown> | null) ?? null);
}

/** Save the project's check list and client switch (one row per project, its id the project's). */
export async function saveBriefSetting(
  sb: SupabaseClient,
  project: Pick<PortalProject, 'id' | 'tenantId'>,
  checks: BriefSettingChecks,
  clientVisible: boolean,
): Promise<void> {
  const { error } = await sb.from('brief_setting').upsert(
    {
      id: project.id,
      tenant_id: project.tenantId,
      project_id: project.id,
      checks: {
        selected: [...checks.selected],
        custom: checks.custom.map((c) => ({ ...c, text: c.text.trim() })),
      },
      client_visible: clientVisible,
      deleted_at: null,
    },
    { onConflict: 'id' },
  );
  if (error) fail(error, 'The pre-flight brief settings could not be saved.');
}
